/**
 * WeQuote CRM — Paid Customers Master Table (Phase 2)
 * Tasks 2.2–2.9: Master data table, filters, import modal, export, row navigation
 */
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiGet, apiPost, apiDelete } from '../../utils/api'

// ─── Types ─────────────────────────────────────────────────────────────────
interface PaidCustomer {
  id: number
  company_name: string
  company_logo_url?: string
  email?: string
  contact_name?: string
  phone?: string
  status: 'active' | 'inactive' | 'vip'
  last_activity_at?: string
  widget_visit_count: number
  account_type?: string
  user_count?: number
  wa_archive_linked?: number
  wa_live_linked?: number
  zoho_linked?: number
  widget_linked?: number
  created_at?: string
}

// ─── Badge Component ───────────────────────────────────────────────────────
const ChannelBadge: React.FC<{ yes: boolean }> = ({ yes }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${
    yes ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
  }`}>
    <span className={`w-1.5 h-1.5 rounded-full ${yes ? 'bg-green-500' : 'bg-gray-300'}`} />
    {yes ? 'Yes' : 'No'}
  </span>
)

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const colours: Record<string, string> = {
    active: 'bg-green-100 text-green-700 border-green-200',
    inactive: 'bg-red-100 text-red-600 border-red-200',
    vip: 'bg-purple-100 text-purple-700 border-purple-200',
  }
  return (
    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${colours[status] || colours.active}`}>
      {status.toUpperCase()}
    </span>
  )
}

// ─── Import Modal ──────────────────────────────────────────────────────────
const ImportModal: React.FC<{ onClose: () => void; onImported: () => void }> = ({ onClose, onImported }) => {
  const [file, setFile] = useState<File | null>(null)
  const [importing, setImporting] = useState(false)
  const [result, setResult] = useState<any>(null)
  const [dragOver, setDragOver] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const handleImport = async () => {
    if (!file) return
    setImporting(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const token = localStorage.getItem('token')
      const resp = await fetch('/api/paid-customers/import', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      })
      const data = await resp.json()
      setResult(data)
      if (data.success) onImported()
    } catch (err: any) {
      setResult({ error: err.message })
    } finally {
      setImporting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Import Customer Details</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        <div className="p-6">
          {!result ? (
            <>
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition ${
                  dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-blue-300'
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]); }}
                onClick={() => fileRef.current?.click()}
              >
                <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} />
                {file ? (
                  <div>
                    <p className="text-sm font-medium text-gray-800">{file.name}</p>
                    <p className="text-xs text-gray-500 mt-1">{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                ) : (
                  <div>
                    <svg className="w-10 h-10 mx-auto text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 16v-8m0 0l-3 3m3-3l3 3M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" /></svg>
                    <p className="text-sm text-gray-600">Drop CSV file here or <span className="text-blue-600 font-medium">browse</span></p>
                    <p className="text-xs text-gray-400 mt-1">Supports .csv exported from Google Sheets</p>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
                <button onClick={handleImport} disabled={!file || importing}
                  className="px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50">
                  {importing ? 'Importing...' : 'Import'}
                </button>
              </div>
            </>
          ) : (
            <div>
              {result.success ? (
                <div className="text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <p className="font-semibold text-gray-900 mb-2">Import Complete</p>
                  <div className="flex justify-center gap-6 text-sm">
                    <div><span className="font-bold text-green-600">{result.summary?.created || 0}</span> <span className="text-gray-500">created</span></div>
                    <div><span className="font-bold text-blue-600">{result.summary?.updated || 0}</span> <span className="text-gray-500">updated</span></div>
                    <div><span className="font-bold text-gray-400">{result.summary?.skipped || 0}</span> <span className="text-gray-500">skipped</span></div>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-red-600 font-medium">Import failed</p>
                  <p className="text-sm text-gray-500 mt-1">{result.error}</p>
                </div>
              )}
              <div className="flex justify-end mt-6">
                <button onClick={onClose} className="px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">Done</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Add Customer Modal ────────────────────────────────────────────────────
const AddCustomerModal: React.FC<{ onClose: () => void; onCreated: () => void }> = ({ onClose, onCreated }) => {
  const [form, setForm] = useState<Record<string, string>>({
    company_name: '', email: '', phone: '', contact_name: '',
    address_line_1: '', city: '', county: '', country: '', postcode: '',
    account_type: '', user_count: '0', stock_module_version: '', status: 'active',
  })
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    if (!form.company_name) return
    setSaving(true)
    try {
      await apiPost('/paid-customers', { ...form, user_count: parseInt(form.user_count) || 0 })
      onCreated()
      onClose()
    } catch (err: any) {
      alert(err.message)
    } finally {
      setSaving(false)
    }
  }

  const Field = ({ label, field, type = 'text' }: { label: string; field: string; type?: string }) => (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <input type={type} value={form[field] || ''} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
    </div>
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold text-gray-900">Add Customer</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        <div className="p-6 space-y-3">
          <Field label="Company Name *" field="company_name" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email" field="email" type="email" />
            <Field label="Phone" field="phone" />
          </div>
          <Field label="Contact Name" field="contact_name" />
          <Field label="Address Line 1" field="address_line_1" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="City" field="city" />
            <Field label="County" field="county" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Country" field="country" />
            <Field label="Postcode" field="postcode" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Account Type" field="account_type" />
            <Field label="Users" field="user_count" type="number" />
            <Field label="Stock Module" field="stock_module_version" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
            <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="vip">VIP</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={handleSave} disabled={!form.company_name || saving}
            className="px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50">
            {saving ? 'Saving...' : 'Add Customer'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main Component ────────────────────────────────────────────────────────
export const PaidCustomersView: React.FC = () => {
  const navigate = useNavigate()
  const [customers, setCustomers] = useState<PaidCustomer[]>([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [newThisMonth, setNewThisMonth] = useState(0)
  const [filter, setFilter] = useState<'all' | 'active' | 'vip'>('all')
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [matchHints, setMatchHints] = useState<Record<number, string[]>>({})
  const [showImport, setShowImport] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  const [contextMenu, setContextMenu] = useState<{ id: number; x: number; y: number } | null>(null)
  const [syncing, setSyncing] = useState(false)
  // Sync progress banner state. Because the sync is a single API round-trip we
  // can't show real percent progress, so we show an indeterminate bar while
  // in-flight and a success/error result afterwards (auto-dismissed).
  const [syncBanner, setSyncBanner] = useState<
    { state: 'progress' } | { state: 'success'; message: string } | { state: 'error'; message: string } | null
  >(null)
  const syncDismissTimer = useRef<ReturnType<typeof setTimeout>>()
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>()

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true)
      const params: Record<string, any> = {}
      if (filter === 'active') params.status = 'active'
      if (filter === 'vip') params.status = 'vip'
      if (search) params.search = search

      const data = await apiGet<any>('/paid-customers', params)
      setCustomers(data.customers || [])
      setTotal(data.total || 0)
      setNewThisMonth(data.newThisMonth || 0)
      setMatchHints(data.matchHints || {})
    } catch (err) {
      console.error('Failed to fetch paid customers:', err)
    } finally {
      setLoading(false)
    }
  }, [filter, search])

  useEffect(() => { fetchCustomers() }, [fetchCustomers])

  // Clear any pending sync-banner auto-dismiss timer on unmount.
  useEffect(() => () => { if (syncDismissTimer.current) clearTimeout(syncDismissTimer.current) }, [])

  const handleSearch = (val: string) => {
    setSearchInput(val)
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(() => { setSearch(val); }, 500)
  }

  const handleExport = () => {
    const token = localStorage.getItem('token')
    window.open(`/api/paid-customers-export?token=${token}`, '_blank')
  }

  const handleSync = async () => {
    if (syncDismissTimer.current) clearTimeout(syncDismissTimer.current)
    setSyncing(true)
    setSyncBanner({ state: 'progress' })
    try {
      const res = await apiPost<any>('/paid-customers/sync-from-wequote', {})
      const created = res?.created ?? 0
      const updated = res?.updated ?? 0
      const people = res?.people ?? 0
      const count = res?.count ?? created + updated
      const message =
        res?.note ||
        (count
          ? `Synced ${count} customers — ${created} added, ${updated} updated${people ? `, ${people} contacts` : ''}.`
          : 'Sync complete.')
      setSyncBanner({ state: 'success', message })
      fetchCustomers()
      syncDismissTimer.current = setTimeout(() => setSyncBanner(null), 5000)
    } catch (err: any) {
      setSyncBanner({
        state: 'error',
        message: err?.message || 'Sync failed. Check that the WeQuote API is configured on the server.',
      })
    } finally {
      setSyncing(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this customer?')) return
    try {
      await apiDelete(`/paid-customers/${id}`)
      fetchCustomers()
    } catch (err: any) {
      alert(err.message)
    }
  }

  const handleRunLinking = async (id: number) => {
    try {
      const result = await apiPost<any>(`/paid-customers/${id}/run-linking`)
      alert(`Linking complete: ${result.linksCreated} new links created`)
      fetchCustomers()
    } catch (err: any) {
      alert(err.message)
    }
  }

  const initials = (name: string) => {
    const parts = name.split(/\s+/)
    return parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : name.slice(0, 2).toUpperCase()
  }

  return (
    <div className="p-6 lg:p-8 w-full animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-txt-primary">Customers</h1>
          <p className="text-sm text-txt-secondary mt-1">Manage your WeQuote paid customer accounts</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={handleSync} disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-txt-secondary bg-surface border border-border rounded-lg hover:bg-surface-hover transition-colors disabled:opacity-60"
            title="Pull all customers from the WeQuote database">
            <svg className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" /></svg>
            {syncing ? 'Syncing…' : 'Sync Customers'}
          </button>
          <button onClick={() => setShowImport(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-txt-secondary bg-surface border border-border rounded-lg hover:bg-surface-hover transition-colors">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 16v-8m0 0l-3 3m3-3l3 3M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" /></svg>
            Import Customer Details
          </button>
          <button onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-txt-secondary bg-surface border border-border rounded-lg hover:bg-surface-hover transition-colors">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v8m0 0l3-3m-3 3l-3-3M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" /></svg>
            Export
          </button>
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            Add Customer
          </button>
        </div>
      </div>

      {/* Sync progress banner — sits full-width between the title and the content */}
      {syncBanner && (
        <div
          className={`mb-4 rounded-lg border px-4 py-3 ${
            syncBanner.state === 'error'
              ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
              : syncBanner.state === 'success'
              ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
              : 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800'
          }`}
        >
          <div className="flex items-center justify-between gap-3">
            <p
              className={`text-sm font-medium ${
                syncBanner.state === 'error'
                  ? 'text-red-700 dark:text-red-300'
                  : syncBanner.state === 'success'
                  ? 'text-green-700 dark:text-green-300'
                  : 'text-blue-700 dark:text-blue-300'
              }`}
            >
              {syncBanner.state === 'progress'
                ? 'Syncing customers from WeQuote…'
                : syncBanner.message}
            </p>
            {syncBanner.state !== 'progress' && (
              <button
                onClick={() => setSyncBanner(null)}
                className="text-txt-secondary hover:text-txt-primary transition-colors"
                aria-label="Dismiss"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            )}
          </div>
          {syncBanner.state === 'progress' && (
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-blue-100 dark:bg-blue-900/40">
              <div className="h-full w-1/3 rounded-full bg-blue-500 animate-indeterminate-bar" />
            </div>
          )}
        </div>
      )}

      {/* Filter bar + Stats */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="flex bg-surface-secondary rounded-lg p-0.5">
            {(['all', 'active', 'vip'] as const).map(f => (
              <button key={f} onClick={() => { setFilter(f); }}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  filter === f ? 'bg-surface text-txt-primary shadow-sm' : 'text-txt-secondary hover:text-txt-primary'
                }`}>
                {f === 'all' ? 'All Users' : f === 'active' ? 'Active Only' : 'VIP'}
              </button>
            ))}
          </div>
          <div className="relative ml-3">
            <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-txt-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            <input type="text" placeholder="Search name, email, ID, alias, chats..."
              value={searchInput}
              onChange={e => handleSearch(e.target.value)}
              className="pl-9 pr-8 py-1.5 text-sm border border-border rounded-lg w-80 focus:ring-2 focus:ring-brand-500 focus:border-transparent bg-surface" />
            {searchInput && (
              <button onClick={() => { setSearchInput(''); setSearch(''); setMatchHints({}); }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-txt-secondary hover:text-txt-primary">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            )}
          </div>
          {search && (
            <span className="ml-2 text-xs text-txt-secondary bg-surface-secondary px-2 py-1 rounded-full">
              Deep search across customers, channels &amp; chat history
            </span>
          )}
        </div>
        <div className="flex items-center gap-6 text-sm">
          <div><span className="text-txt-secondary">Total Customers</span> <span className="ml-2 text-lg font-bold text-txt-primary">{total.toLocaleString()}</span></div>
          <div><span className="text-txt-secondary">New this month</span> <span className="ml-2 text-lg font-bold text-green-600">+{newThisMonth}</span></div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface rounded-xl border border-border overflow-x-auto shadow-sm">
        <table className="w-full min-w-[960px]">
          <thead>
            <tr className="border-b border-border bg-surface-secondary">
              <th className="text-left px-6 py-3.5 text-xs font-semibold text-txt-secondary uppercase tracking-wider">Company Name</th>
              <th className="text-left px-6 py-3.5 text-xs font-semibold text-txt-secondary uppercase tracking-wider">Email Address</th>
              <th className="text-left px-6 py-3.5 text-xs font-semibold text-txt-secondary uppercase tracking-wider">Status</th>
              <th className="text-left px-6 py-3.5 text-xs font-semibold text-txt-secondary uppercase tracking-wider">Last Activity</th>
              <th className="text-center px-5 py-3.5 text-xs font-semibold text-txt-secondary uppercase tracking-wider leading-tight">Archived<br/>WhatsApp Group</th>
              <th className="text-center px-5 py-3.5 text-xs font-semibold text-txt-secondary uppercase tracking-wider leading-tight">Live<br/>WhatsApp Group</th>
              <th className="text-center px-5 py-3.5 text-xs font-semibold text-txt-secondary uppercase tracking-wider leading-tight">Zoho<br/>Archived Chat</th>
              <th className="text-center px-5 py-3.5 text-xs font-semibold text-txt-secondary uppercase tracking-wider leading-tight">Live Chat<br/>Widget Count</th>
              <th className="w-12 sticky right-0 bg-surface-secondary"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="py-16 text-center text-sm text-txt-secondary">Loading customers...</td></tr>
            ) : customers.length === 0 ? (
              <tr><td colSpan={9} className="py-16 text-center text-sm text-txt-secondary">No customers found. Import a CSV to get started.</td></tr>
            ) : customers.map((c) => (
              <tr key={c.id} className="hover:bg-surface-hover cursor-pointer transition-colors duration-100"
                onClick={() => navigate(`/customers/${c.id}`)}>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-brand-100 text-brand-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {initials(c.company_name)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-txt-primary">{c.company_name}</p>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-xs text-txt-secondary">ID: {(c as any).wequote_id ? `#${(c as any).wequote_id}` : `#C-${String(c.id).padStart(4, '0')}`}</p>
                        {(c as any).alias && (
                          <span className="inline-flex items-center px-1.5 py-px rounded text-[10px] font-medium bg-slate-100 text-slate-600">
                            {(c as any).alias}
                          </span>
                        )}
                        {search && matchHints[c.id] && matchHints[c.id].map((hint: string, hi: number) => (
                          <span key={hi} className={`inline-flex items-center px-1.5 py-px rounded text-[9px] font-semibold ${
                            hint.startsWith('chat content') ? 'bg-purple-100 text-purple-700' :
                            hint.startsWith('zoho') ? 'bg-orange-100 text-orange-700' :
                            hint.startsWith('wa ') ? 'bg-green-100 text-green-700' :
                            hint.startsWith('widget') ? 'bg-blue-100 text-blue-700' :
                            hint === 'wequote id' || hint === 'alias' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            <svg className="w-2.5 h-2.5 mr-0.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" /></svg>
                            {hint}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-txt-secondary">{c.email || '—'}</td>
                <td className="px-6 py-4"><StatusBadge status={c.status} /></td>
                <td className="px-6 py-4 text-sm text-txt-secondary">
                  {c.last_activity_at ? new Date(c.last_activity_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                </td>
                <td className="px-5 py-4 text-center"><ChannelBadge yes={(c.wa_archive_linked || 0) > 0} /></td>
                <td className="px-5 py-4 text-center"><ChannelBadge yes={(c.wa_live_linked || 0) > 0} /></td>
                <td className="px-5 py-4 text-center"><ChannelBadge yes={(c.zoho_linked || 0) > 0} /></td>
                <td className="px-5 py-4 text-center">
                  <span className="text-sm font-medium text-brand-600">{c.widget_visit_count || 0} Visits</span>
                </td>
                <td className="px-2 py-4 text-center sticky right-0 bg-surface" onClick={e => e.stopPropagation()}>
                  <button onClick={(e) => {
                      if (contextMenu?.id === c.id) { setContextMenu(null); return; }
                      const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                      setContextMenu({ id: c.id, x: r.right, y: r.bottom });
                    }}
                    aria-label="Row actions"
                    className="p-1.5 rounded-md text-txt-secondary hover:text-txt-primary hover:bg-surface-hover border border-transparent hover:border-border transition-colors">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M10 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4z" /></svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Full list — pagination removed; the whole customer list is shown at once. */}
      {!loading && total > 0 && (
        <div className="mt-4 text-sm text-txt-secondary">
          <p>Showing all {total.toLocaleString()} clients</p>
        </div>
      )}

      {/* Context menu — rendered fixed so the table's overflow-hidden can't clip it */}
      {contextMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setContextMenu(null)} />
          <div className="fixed w-44 bg-surface border border-border rounded-lg shadow-lg z-50 py-1"
            style={{ top: contextMenu.y + 4, left: Math.max(8, contextMenu.x - 176) }}>
            <button onClick={() => { navigate(`/customers/${contextMenu.id}`); setContextMenu(null); }}
              className="w-full text-left px-4 py-2 text-sm text-txt-primary hover:bg-surface-hover">View Details</button>
            <button onClick={() => { handleRunLinking(contextMenu.id); setContextMenu(null); }}
              className="w-full text-left px-4 py-2 text-sm text-txt-primary hover:bg-surface-hover">Re-run Linking</button>
            <button onClick={() => { handleDelete(contextMenu.id); setContextMenu(null); }}
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50">Delete</button>
          </div>
        </>
      )}

      {/* Modals */}
      {showImport && <ImportModal onClose={() => setShowImport(false)} onImported={fetchCustomers} />}
      {showAdd && <AddCustomerModal onClose={() => setShowAdd(false)} onCreated={fetchCustomers} />}
    </div>
  )
}
